class SodiiSplitStoryParallax extends HTMLElement {
  #detailsSection;
  #detailsStrip;
  #desktopMediaQuery;
  #frame;
  #mediaQuery;
  #product;
  #productImage;
  #resizeObserver;
  #sectionObserver;
  #stage;

  connectedCallback() {
    this.#stage = this.querySelector('[data-sodii-parallax-stage]');
    this.#product = this.querySelector('[data-sodii-parallax-product]');
    this.#productImage = this.#product?.querySelector('img');

    if (!this.#stage || !this.#product) return;

    this.#mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#desktopMediaQuery = window.matchMedia('(min-width: 750px)');
    if ('addEventListener' in this.#mediaQuery) {
      this.#mediaQuery.addEventListener('change', this.#queueUpdate);
    } else {
      this.#mediaQuery.addListener(this.#queueUpdate);
    }
    if ('addEventListener' in this.#desktopMediaQuery) {
      this.#desktopMediaQuery.addEventListener('change', this.#queueUpdate);
    } else {
      this.#desktopMediaQuery.addListener(this.#queueUpdate);
    }

    window.addEventListener('scroll', this.#queueUpdate, { passive: true });
    window.addEventListener('resize', this.#queueUpdate, { passive: true });
    this.#productImage?.addEventListener('load', this.#queueUpdate);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#queueUpdate);
      this.#resizeObserver.observe(this);
      this.#resizeObserver.observe(this.#stage);
      this.#resizeObserver.observe(this.#product);
    }

    const sectionWrapper = this.closest('.sodii-split-parallax-section');
    if ('MutationObserver' in window && sectionWrapper?.parentElement) {
      this.#sectionObserver = new MutationObserver(this.#queueUpdate);
      this.#sectionObserver.observe(sectionWrapper.parentElement, { childList: true });
    }

    this.#refreshDetailsCompanion();
    this.#queueUpdate();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#queueUpdate);
    window.removeEventListener('resize', this.#queueUpdate);
    this.#productImage?.removeEventListener('load', this.#queueUpdate);
    if (this.#mediaQuery && 'removeEventListener' in this.#mediaQuery) {
      this.#mediaQuery.removeEventListener('change', this.#queueUpdate);
    } else {
      this.#mediaQuery?.removeListener(this.#queueUpdate);
    }
    if (this.#desktopMediaQuery && 'removeEventListener' in this.#desktopMediaQuery) {
      this.#desktopMediaQuery.removeEventListener('change', this.#queueUpdate);
    } else {
      this.#desktopMediaQuery?.removeListener(this.#queueUpdate);
    }
    this.#releaseDetailsCompanion();
    this.#resizeObserver?.disconnect();
    this.#sectionObserver?.disconnect();

    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  #queueUpdate = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  };

  #update() {
    if (!this.#stage || !this.#product) return;

    this.#refreshDetailsCompanion();

    const trackRect = this.getBoundingClientRect();
    this.#updateDetailsLayout(trackRect.height);

    const stageHeight = this.#stage.getBoundingClientRect().height;
    const productHeight = this.#product.getBoundingClientRect().height;

    if (!stageHeight || !productHeight) return;

    const scrollRange = Math.max(1, trackRect.height - stageHeight);
    const progress = this.#mediaQuery?.matches ? 0.5 : Math.min(1, Math.max(0, -trackRect.top / scrollRange));
    const startGap = Number.parseFloat(this.dataset.startGap || '0') || 0;
    const endGap = Number.parseFloat(this.dataset.endGap || '0') || 0;
    const startCenter = stageHeight + productHeight / 2 + startGap;
    const endCenter = -(productHeight / 2) - endGap;
    const centerPosition = startCenter + (endCenter - startCenter) * progress;
    const offset = centerPosition - stageHeight / 2;

    this.style.setProperty('--sodii-parallax-product-offset', `${offset.toFixed(2)}px`);
    this.style.setProperty('--sodii-parallax-progress', progress.toFixed(4));
  }

  #refreshDetailsCompanion() {
    const sectionWrapper = this.closest('.sodii-split-parallax-section');
    const nextSection = sectionWrapper?.nextElementSibling;
    const detailsSection = nextSection?.matches('.sodii-product-details-strip-section') ? nextSection : undefined;
    const detailsStrip = detailsSection?.querySelector('.sodii-product-details-strip');

    if (detailsSection === this.#detailsSection && detailsStrip === this.#detailsStrip) return;

    this.#releaseDetailsCompanion();
    this.#detailsSection = detailsSection;
    this.#detailsStrip = detailsStrip;
    if (this.#detailsStrip) this.#resizeObserver?.observe(this.#detailsStrip);
  }

  #updateDetailsLayout(trackHeight) {
    const isPaired = this.#desktopMediaQuery?.matches && this.#detailsSection && this.#detailsStrip;

    if (!isPaired) {
      this.#clearDetailsLayout();
      return;
    }

    const detailsHeight = this.#detailsStrip.getBoundingClientRect().height;
    if (!trackHeight || !detailsHeight) return;

    this.setAttribute('data-sodii-with-details', '');
    this.style.setProperty('--sodii-parallax-details-height', `${detailsHeight.toFixed(2)}px`);
    this.#detailsSection.setAttribute('data-sodii-parallax-companion', '');
    this.#detailsSection.style.setProperty('--sodii-parallax-companion-track-height', `${trackHeight.toFixed(2)}px`);
    this.#detailsSection.style.setProperty('--sodii-parallax-companion-height', `${detailsHeight.toFixed(2)}px`);
  }

  #clearDetailsLayout() {
    this.removeAttribute('data-sodii-with-details');
    this.style.removeProperty('--sodii-parallax-details-height');
    this.#detailsSection?.removeAttribute('data-sodii-parallax-companion');
    this.#detailsSection?.style.removeProperty('--sodii-parallax-companion-track-height');
    this.#detailsSection?.style.removeProperty('--sodii-parallax-companion-height');
  }

  #releaseDetailsCompanion() {
    this.#clearDetailsLayout();
    if (this.#detailsStrip) this.#resizeObserver?.unobserve(this.#detailsStrip);
    this.#detailsSection = undefined;
    this.#detailsStrip = undefined;
  }
}

if (!customElements.get('sodii-split-story-parallax')) {
  customElements.define('sodii-split-story-parallax', SodiiSplitStoryParallax);
}
